'use client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Sidebar, useSidebar } from '@/core/ui/primitives/sidebar';
import { LuBoxes, LuBuilding2, LuSettings } from 'react-icons/lu';
import { OrganizationSidebarOrgSwitcher, type SidebarOrganizationOption } from './OrganizationSidebarOrgSwitcher';
import { OrganizationSidebarNav } from './OrganizationSidebarNav';
interface OrganizationOption {
  id: string;
  name: string;
  plan?: string;
  icon?: typeof LuBuilding2;
  slug?: string;
  logoUrl?: string | null;
}
export type OrganizationNavItem = {
  title: string;
  url: string;
  icon?: typeof LuBuilding2;
  isActive?: boolean;
  items?: Array<{
    title: string;
    url: string;
    vaultId?: string;
    vaultSlug?: string;
    organizationId?: string;
    /** True if vault is soft-deleted (in trash) */
    isDeleted?: boolean;
  }>;
};
export interface OrganizationSidebarSelection {
  section: string;
  label: string;
  vaultId?: string | null;
  vaultSlug?: string | null;
  organizationId?: string | null;
  /** True if vault is soft-deleted (in trash) */
  isDeleted?: boolean;
}
interface OrganizationSidebarProps {
  navItems?: OrganizationNavItem[];
  selectedNav?: OrganizationSidebarSelection | null;
  onNavSelect?: (selection: OrganizationSidebarSelection) => void;
  organizations?: OrganizationOption[];
  activeOrganizationId?: string | null;
  onOrganizationChange?: (organizationId: string) => void;
  loading?: boolean;
  error?: string | null;
  onOrganizationCreate?: (name: string) => Promise<unknown>;
  canCreateOrganization?: boolean;
  organizationLimit?: number;
  ownedOrganizationCount?: number;
}
// Note: No mock fallbacks. Components render explicit empty/error states when data is unavailable.
export function OrganizationSidebar({
  navItems,
  selectedNav,
  onNavSelect,
  organizations,
  activeOrganizationId,
  onOrganizationChange,
  loading,
  error,
  onOrganizationCreate,
  canCreateOrganization,
  organizationLimit,
  ownedOrganizationCount,
}: OrganizationSidebarProps) {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const availableOrganizations = useMemo<SidebarOrganizationOption[]>(() => {
    const source = organizations && organizations.length ? organizations : [];
    return source.map((org) => ({
      ...org,
      icon: org.icon ?? LuBuilding2,
      plan: org.plan ?? 'Active',
    }));
  }, [organizations]);
  const isControlled = activeOrganizationId !== undefined;
  const [internalOrgId, setInternalOrgId] = useState<string | null>(() => availableOrganizations[0]?.id ?? null);
  useEffect(() => {
    if (isControlled) {
      return;
    }
    const firstId = availableOrganizations[0]?.id ?? null;
    setInternalOrgId((previous) => {
      if (previous && availableOrganizations.some((org) => org.id === previous)) {
        return previous;
      }
      return firstId;
    });
  }, [availableOrganizations, isControlled]);
  const effectiveOrgId = isControlled
    ? activeOrganizationId ?? availableOrganizations[0]?.id ?? null
    : internalOrgId ?? availableOrganizations[0]?.id ?? null;
  const activeOrganization = useMemo(() => {
    if (!availableOrganizations.length) {
      return null;
    }
    if (effectiveOrgId) {
      const matched = availableOrganizations.find((org) => org.id === effectiveOrgId);
      if (matched) {
        return matched;
      }
    }
    return availableOrganizations[0];
  }, [availableOrganizations, effectiveOrgId]);
  const handleOrganizationSelect = useCallback(
    (organizationId: string) => {
      if (!isControlled) {
        setInternalOrgId(organizationId);
      }
      onOrganizationChange?.(organizationId);
    },
    [isControlled, onOrganizationChange]
  );
  const items = useMemo(() => navItems ?? [], [navItems]);
  useEffect(() => {
    if (!selectedNav && items.length > 0) {
      const first = items[0];
      if (first.items?.length) {
        const primary = first.items[0];
        onNavSelect?.({
          section: first.title,
          label: primary.title,
          vaultId: primary.vaultId ?? null,
          vaultSlug: primary.vaultSlug ?? null,
          organizationId: primary.organizationId ?? null,
        });
      } else {
        onNavSelect?.({ section: first.title, label: first.title });
      }
    }
  }, [items, onNavSelect, selectedNav]);
  const selectedKey = useMemo(() => {
    if (!selectedNav) return null;
    if (selectedNav.vaultId) {
      return `${selectedNav.section}|${selectedNav.vaultId}`;
    }
    return `${selectedNav.section}|${selectedNav.label}`;
  }, [selectedNav]);
  return (
    <Sidebar className='bg-sidebar dark:bg-sidebar-dark/80'>
      <div className='flex h-full flex-1 flex-col'>
        <OrganizationSidebarOrgSwitcher
          collapsed={collapsed}
          organizations={availableOrganizations}
          activeOrganizationId={effectiveOrgId}
          onSelect={handleOrganizationSelect}
          onCreateOrganization={onOrganizationCreate}
          canCreateOrganization={canCreateOrganization}
          organizationLimit={organizationLimit}
          ownedOrganizationCount={ownedOrganizationCount}
        />
        <OrganizationSidebarNav
          items={items}
          onSelect={onNavSelect}
          selectedKey={selectedKey}
          loading={loading}
          error={error}
          activeOrganizationId={activeOrganization?.id ?? null}
          activeOrganizationSlug={activeOrganization?.slug ?? null}
        />
      </div>
    </Sidebar>
  );
}
